/**
 * API shape of `GET /demand/{id}/selectable-currencies` (EPROC-11425).
 *
 * `customerCurrencies` is optional on purpose: an API that predates EPROC-11425 does not send the
 * field at all, and "unknown" must never be read as "the customer allows nothing" — during a
 * rolling deploy that would empty the currency dropdown.
 */
export interface IDemandSelectableCurrencies {
    selectableCurrencies: string[];
    customerCurrencies?: string[];
    demandCurrency: string | null;
    accepted: boolean;
}

/** The active currencies grouped by the reason they cannot be used on a demand (EPROC-11425). */
export interface ICurrencyExclusions {
    /** Not among the accepted currencies of the customer. */
    readonly byCustomer: Set<string>;
    /** Allowed by the customer, but not accepted by every supplier of the demand. */
    readonly bySupplier: Set<string>;
}

/**
 * Currencies a demand may be written in: the currencies the customer shares with every supplier
 * already assigned to the demand. While no supplier is selected these are all currencies of the
 * customer — the supplier only narrows the choice (EPROC-11425).
 */
export class DemandSelectableCurrencies {
    public readonly currencies: string[];
    /**
     * The customer's currencies without the supplier restriction, or `undefined` when the API did
     * not report them. Distinguishes WHY a currency is not selectable: missing here → the customer
     * does not allow it; present here but not in `currencies` → a supplier does not accept it.
     */
    public readonly customerCurrencies?: string[];
    public readonly demandCurrency?: string;
    /** False when the demand currently uses a currency its suppliers do not accept. */
    public readonly accepted: boolean;

    constructor(dto: IDemandSelectableCurrencies) {
        this.currencies = dto.selectableCurrencies ?? [];
        this.customerCurrencies = dto.customerCurrencies;
        this.demandCurrency = dto.demandCurrency ?? undefined;
        this.accepted = dto.accepted;
    }

    /**
     * True when the customer itself does not allow the given currency (EPROC-11425 reopen #2) —
     * the conflict message must name the actual party instead of blaming the suppliers, e.g. for a
     * document created from PDF/AI import in a currency outside the customer's master data.
     * Unknown customer data (`undefined` or empty, see class doc) never accuses anyone.
     */
    public customerRejects(iso: string | null | undefined): boolean {
        return !!iso && !!this.customerCurrencies?.length && !this.customerCurrencies.includes(iso);
    }

    /**
     * Splits the given active currencies by the reason they are not selectable, so a dropdown can
     * hide them and the hint below the field can name the real reason.
     *
     * Absence of data never restricts: a missing lookup flags nothing, and an absent or empty
     * `customerCurrencies` counts as "customer unrestricted" — the same fallback the release gate
     * applies to a tenant without maintained currencies (`DemandCurrencyAcceptanceChecker`,
     * eproc-api). The hard block stays server-side at the order conversion.
     */
    public static exclusionsAmong(
        activeIsos: string[],
        selectable?: DemandSelectableCurrencies,
    ): ICurrencyExclusions {
        if (!selectable) {
            return { byCustomer: new Set<string>(), bySupplier: new Set<string>() };
        }

        const allowedByCustomer = new Set(
            selectable.customerCurrencies?.length ? selectable.customerCurrencies : activeIsos,
        );

        return {
            byCustomer: new Set(activeIsos.filter((iso) => !allowedByCustomer.has(iso))),
            bySupplier: new Set(
                activeIsos.filter((iso) => allowedByCustomer.has(iso) && !selectable.currencies.includes(iso)),
            ),
        };
    }
}
